/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/
 
import { HTMLAttributes } from 'preact/compat';
import { useMemo, useState, useEffect } from 'preact/hooks';
import { Container } from '@adobe-commerce/elsie/lib';
import { Picker } from '@adobe-commerce/elsie/components';
import { events } from '@adobe-commerce/event-bus';
import { useCompanyData } from '@/company-switcher/hooks/useCompanyData';
import { Company } from '@/company-switcher/types/company';

export interface CompanySwitcherProps extends HTMLAttributes<HTMLDivElement> {
  /** Custom aria-label for the picker */
  ariaLabel?: string;
  /** Callback function to be called when the company changes */
  onCompanyChange?: (company: Company) => void;
  /** Maximum number of companies to fetch for the picker */
  size?: number;
}

/**
 * CompanySwitcher component allows users to switch between companies they have access to.
 * It only renders when a user has access to multiple companies.
 * This is a presentational component that uses the useCompanyData hook for all business logic.
 */
export const CompanySwitcher: Container<CompanySwitcherProps> = ({
  ariaLabel,
  onCompanyChange,
  size = 100,
  ...props
}) => {
  const { companies, currentCompany, handleCompanyChange } = useCompanyData({ onCompanyChange, pageSize: size });
  const [isVisible, setIsVisible] = useState(true);

  // Memoize the companies array to prevent unnecessary re-renders
  const memoizedCompanies = useMemo(() => companies, [companies]);

  // Listen for checkout/initialized and checkout/updated events to hide the picker
  useEffect(() => {
    const hidePickerHandler = () => {
      setIsVisible(false);
    };

    const unsubscribeInitialized = events.on('checkout/initialized', hidePickerHandler);
    const unsubscribeUpdated = events.on('checkout/updated', hidePickerHandler);

    return () => {
      unsubscribeInitialized?.off();
      unsubscribeUpdated?.off();
    };
  }, []);

  // Only render if user has access to multiple companies and picker is visible
  if (companies.length < 2 || !isVisible) {
    return null;
  }

  return (
    <div {...props}>
      <Picker 
        options={memoizedCompanies} 
        value={currentCompany.value} 
        onChange={handleCompanyChange}
        aria-label={ariaLabel || 'Select company'}
        aria-describedby="company-switcher-description"
      />
    </div>
  );
};
